/**
 * SalesAuditStrip.tsx — the Slice B audit strip (§15.5 frontend surface).
 *
 * One reverse-chronological list per record: `sales_field_history` for every
 * non-stage field edit, plus `sales_deal_stage_events` for stage transitions
 * on a deal. The two reads stay SEPARATE on the wire (B-1) and are composed
 * client-side in auditStrip.ts — see the ordering rule there.
 *
 * DATA HONESTY, the whole point of this component:
 *  - A row with no actor renders as an unknown user. The six pre-0117 stage
 *    events were made by real people whose identity was never recorded, so
 *    "system" would be a fabrication (B-2) and a blank would hide a real
 *    change. Neither is acceptable; the strip says it does not know.
 *  - An attributed row shows the actor's display name when the read path
 *    carried one (§15.12 AN-3: resolved server-side, display_name only, never
 *    an email) and the SHORT user id when it did not; the full id is always
 *    in the title. The ladder lives in auditStrip.ts (actorFor/actorLabel) —
 *    the single change point AN-4 names.
 *  - `assigned_employee_id` and `account_id` resolve to names through a
 *    FRONTEND join. A join target that has gone (a deleted account, a leaver
 *    filtered out of the roster) falls back to the raw id, never a blank.
 *  - NULL and "" are different stored values (B-5) and render differently.
 *
 * Looks: ADOPTION.md §3.4 — `.k-card` with `.k-card-head`, rows on
 * `.k-cell-muted` weights, the Deal Detail timeline's 8x8 icon medallion so
 * the strip reads as a sibling of the activity timeline it sits under.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { ArrowRight, Layers, Pencil, Plus } from 'lucide-react'
import { relAgo, fmtDate } from '../../api'
import { useAuditFeed } from './auditFeed'
import {
  fieldLabel, renderValue, stageLabel, actorFor, actorLabel, codedValue,
  type AuditRow, type AuditJoins, type RenderedValue,
} from './auditStrip'
import { suggestionLabel, logRawCopy } from './dealCopy'

/** The birth row's noun, per record (spec §3.0 / §4: "Record created" was a
 *  developer's word for three different things). */
const RECORD_NOUN: Record<'deals' | 'contacts' | 'accounts', string> = { deals: 'Deal', contacts: 'Contact', accounts: 'Account' }
/** The server-written suggestion row (forecast_accept.go: "rule N: reason"). */
const SUGGESTION_FIELD = 'forecast_category_suggestion'
/** Accounts S6: the survivor's merge row — the merged account's NAME, set by
 *  a merge and cleared by its Undo (account_merge.go). */
const MERGED_ACCOUNT_FIELD = 'merged_account'

const KIND_ICON = {
  stage: <Layers size={13} />,
  field: <Pencil size={13} />,
  created: <Plus size={13} />,
}

/** An empty value is styled apart from a real one, and the two KINDS of empty
 *  are labelled apart from each other (B-5). */
function Value({ v, field, joins }: { v: RenderedValue; field: string; joins: AuditJoins }) {
  // Spec §4 "no developer strings" — slice 1 mapped these on the deal page;
  // slice 2 extends the plain copy to the account, contact and lead pages,
  // keeping the B-5 distinction between the two empties in plain words.
  if (v.kind === 'empty') return <span className="text-ink-muted italic">not set</span>
  if (v.kind === 'blank') return <span className="text-ink-muted italic">left blank</span>
  // An id-valued field whose join target is gone (a leaver, a deleted account,
  // a roster read that failed) still shows that a change happened — in words,
  // never as the raw id. The raw value is logged.
  if (field === 'assigned_employee_id' || field === 'account_id') {
    const resolved = field === 'assigned_employee_id' ? joins.employeeName?.(v.text) : joins.accountName?.(v.text)
    if (!resolved) {
      logRawCopy(`history ${field}`, v.text)
      return <span className="text-ink-muted italic">{field === 'account_id' ? 'an account not on file' : 'someone not on the roster'}</span>
    }
    return <span className="text-ink-primary">{resolved}</span>
  }
  // Account detail v3 AD0 (§45): a region or sector code reads as words, the
  // code kept beside it in the muted weight. A code this bundle does not know
  // shows as stored, and is logged.
  if (field === 'region' || field === 'sector') {
    const coded = codedValue(field, v.text)
    if (!coded) {
      logRawCopy(`history ${field}`, v.text)
      return <span className="text-ink-primary">{v.text}</span>
    }
    return (
      <span className="text-ink-primary">
        {coded.label}{coded.code && <span className="text-ink-muted"> ({coded.code})</span>}
      </span>
    )
  }
  return <span className="text-ink-primary">{v.text}</span>
}

/** A stage slug as words; an unmapped slug is logged, never shown. */
function plainStage(slug: string): string {
  const label = stageLabel(slug)
  if (label !== slug) return label
  logRawCopy('stage', slug)
  return 'another stage'
}

/** One change row. Exported for the account page's merged timeline (Account
 *  detail v3 AD1), which renders the same rows beside its activities. */
export function AuditRowItem({ row, joins, noun }: { row: AuditRow; joins: AuditJoins; noun: string }) {
  const actor = actorFor(row)
  const who = actorLabel(actor)

  let title: React.ReactNode
  let detail: React.ReactNode = null

  if (row.kind === 'created') {
    // The birth row is a DIFFERENT SHAPE from a field change on purpose: it
    // has no before and no after, and reading it as "created → null" would
    // be nonsense.
    title = <>{noun} created</>
  } else if (row.kind === 'stage') {
    title = row.oldValue === null
      ? <>Created in {plainStage(row.newValue ?? '')}</>
      : <>Stage {plainStage(row.oldValue)} <ArrowRight size={11} className="inline align-middle text-ink-faint" /> {plainStage(row.newValue ?? '')}</>
  } else if (row.field === MERGED_ACCOUNT_FIELD) {
    title = row.newValue !== null
      ? <>Merged “{row.newValue}” into this account</>
      : <>Undid the merge of “{row.oldValue ?? ''}”</>
  } else if (row.field === SUGGESTION_FIELD) {
    // "rule 6: Early stage" is the server's audit value, not copy: the rule is
    // named in plain words and the raw text is logged (dealCopy.ts).
    title = <>Forecast category suggested</>
    detail = <div className="text-12 mt-1 text-ink-secondary">{suggestionLabel(row.newValue)}</div>
  } else {
    title = <>{fieldLabel(row.field)} changed</>
    detail = (
      <div className="text-12 mt-1 flex items-center gap-2 flex-wrap">
        <Value v={renderValue(row.field, row.oldValue)} field={row.field} joins={joins} />
        <ArrowRight size={11} className="text-ink-faint flex-shrink-0" />
        <Value v={renderValue(row.field, row.newValue)} field={row.field} joins={joins} />
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-muted flex-shrink-0">
        {KIND_ICON[row.kind]}
      </div>
      <div className="min-w-0">
        <div className="text-13 font-semibold text-ink-primary">{title}</div>
        {detail}
        <div className="text-11 text-ink-muted mt-1">
          <span
            className={actor.kind === 'unattributed' ? 'italic' : undefined}
            title={actor.kind === 'unattributed' ? 'This change predates recorded attribution'
              : actor.displayName ? undefined : 'This user has no name on file'}
          >{who}</span>
          {' · '}{relAgo(row.at)} · {fmtDate(row.at)}
        </div>
      </div>
    </div>
  )
}

export function SalesAuditStrip({ recordType, recordId, withStageEvents = false, variant = 'card' }: {
  recordType: 'deals' | 'contacts' | 'accounts'
  recordId: string
  /** Deals only: also read and compose the stage transitions (B-1 keeps the
   *  two reads separate, so this is a SECOND request, never a merged one). */
  withStageEvents?: boolean
  /** 'card' = a standalone `.k-card` for the two detail PAGES. 'bare' = rows
   *  only, for the contact drawer, whose sections already carry their own
   *  `.k-eyebrow` heading — a card inside a drawer section would double the
   *  title and break the drawer's flat rhythm. */
  variant?: 'card' | 'bare'
}) {
  const { rows, joins, loading, failed } = useAuditFeed(recordType, recordId, withStageEvents)

  const body = (
    <>
      {loading && <div className="text-13 text-ink-muted">Loading history…</div>}

      {!loading && failed && (
        <div className="text-13 text-ink-muted">
          Change history unavailable.
        </div>
      )}

      {!loading && !failed && rows.length === 0 && (
        <div className="text-13 text-ink-muted">
          No changes recorded.
          <div className="text-12 text-ink-muted mt-1">
            Records last edited before change history was switched on have no entries.
          </div>
        </div>
      )}

      {!loading && !failed && rows.map(r => <AuditRowItem key={r.key} row={r} joins={joins} noun={RECORD_NOUN[recordType]} />)}
    </>
  )

  if (variant === 'bare') {
    return <div className="flex flex-col gap-4 pb-2">{body}</div>
  }

  return (
    <div className="k-card">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Change history</div>
          <div className="k-card-sub">
            {withStageEvents ? 'Field edits and stage transitions' : 'Field edits'}
          </div>
        </div>
      </div>
      <div className="p-5 flex flex-col gap-4">{body}</div>
    </div>
  )
}
